//
//  BloodPressureHistory.viewModel.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getBloodPressureList } from "../network/api";

import dayjs from "dayjs";
import { DATE_STRING_FORMAT } from "../utils/time";
import { BloodPressureData } from "../component/BloodPressureScatterChart";
import { BloodPressure } from "../network/type";
import { BloodPressureChartData } from "../component/BloodPressureChart";

export const useBloodPressureHistory = (
  fitinsightUniqueId: string | null,
  companyId: string | null,
  measurementDateString: string | null
) => {
  const navigate = useNavigate();

  // 散布図データ
  const [scatterChartData, setScatterChartData] = useState<{
    isError: boolean;
    series: BloodPressureData[];
  }>({
    isError: false,
    series: [],
  });

  // 推移グラフータ
  const [transitionChartData, setTransitionChartData] = useState<{
    isError: boolean;
    series: BloodPressureChartData[];
  }>({
    isError: false,
    series: [],
  });

  // サマリーデータ
  const [summaryData, setSummaryData] = useState<{
    max: number;
    min: number;
    pulse: number;
  }>({
    max: 0,
    min: 0,
    pulse: 0,
  });

  useEffect(() => {
    if (!fitinsightUniqueId || !companyId || !measurementDateString) {
      console.error("location.state is not found");
      navigate(-1);
      return;
    }
    // 過去の推移データを初期化する
    initTransitionData();
    // 測定日のデータを初期化する
    initMeasurementDateData();
  }, []);

  /**
   * 日付をYYYY-MM-DD -> YYYY年MM月DD日に変換
   * NOTE: この変換は一旦ここにおいているが必要であればUtil化しても良いかもしれない
   **/
  const convertDateStringToJapaneseFormat = useCallback(
    (dateString: string) => {
      const year = dateString.slice(0, 4);
      const month = dateString.slice(5, 7);
      const day = dateString.slice(8, 10);
      return `${year}年${month}月${day}日`;
    },
    []
  );

  const japaneseFormatDateString = useMemo(() => {
    if (!measurementDateString) return "";
    return convertDateStringToJapaneseFormat(measurementDateString);
  }, [measurementDateString]);

  // MARK: Initialize Data

  /**
   * 直近2週間のグラフデータの構築
   */
  const initTransitionData = useCallback(async () => {
    if (!fitinsightUniqueId || !companyId || !measurementDateString) {
      return;
    }

    // 2週間前
    const startDate = dayjs().subtract(2, "week").format(DATE_STRING_FORMAT);

    // 当日
    const endDate = dayjs().format(DATE_STRING_FORMAT);

    // 当日から2週間の血圧データを取得する
    const bloodPressureData = await getBloodPressureData(
      startDate,
      endDate,
      companyId,
      fitinsightUniqueId
    );

    // シリーズデータの構築
    const transitionChartSeries = buildTransitionChartSeries(
      bloodPressureData.data
    );

    setTransitionChartData({
      series: transitionChartSeries,
      isError: bloodPressureData.isError,
    });
  }, [fitinsightUniqueId, companyId, measurementDateString]);

  /**
   * 測定日のデータを構築する
   */
  const initMeasurementDateData = useCallback(async () => {
    // 測定日が設定されていない場合は何もしない
    if (!measurementDateString || !companyId || !fitinsightUniqueId) return;

    // 測定日の血圧データを取得する
    const bloodPressureData = await getBloodPressureData(
      measurementDateString,
      measurementDateString,
      companyId,
      fitinsightUniqueId
    );

    // シリーズデータの構築
    const scatterChartSeries = buildScatterChartSeries(bloodPressureData.data);

    setScatterChartData({
      series: scatterChartSeries,
      isError: bloodPressureData.isError,
    });

    // 測定情報がある場合のみサマリー表示データをセットする
    // 測定情報は基本的には存在するがクエリパラメータの書き換えなどで存在しないことがあるためこのようにしている
    if (bloodPressureData.data.length) {
      setSummaryData({
        max: bloodPressureData.data[0].max,
        min: bloodPressureData.data[0].min,
        pulse: bloodPressureData.data[0].pulse,
      });
    }
  }, [measurementDateString]);

  /**
   * 血圧データを取得する
   * @param startDate 範囲の始点
   * @param endDate 範囲の終点
   * @param companyId 企業ID
   * @param fitinsightUniqueId FITINSIGHT_ID
   **/
  const getBloodPressureData = useCallback(
    async (
      startDate: string,
      endDate: string,
      companyId: string,
      fitinsightUniqueId: string
    ) => {
      // API経由で指定範囲のデータを取得する
      const response = await getBloodPressureList(
        startDate,
        endDate,
        companyId,
        fitinsightUniqueId
      );

      // レスポンスが200以外の場合はエラーとする
      if (response.status !== 200 || !response.data) {
        return {
          data: [],
          isError: true,
        };
      }

      return {
        data: response.data,
        isError: false,
      };
    },
    []
  );

  /**
   * 散布図グラフのシリーズデータを構築する
   * @param bloodPressureData 血圧データ
   * @returns シリーズデータ
   */
  const buildScatterChartSeries = useCallback(
    (bloodPressureData: BloodPressure[]) => {
      return bloodPressureData.map((item) => {
        return {
          dateString: item.dateString,
          x: item.min,
          y: item.max,
        };
      });
    },
    []
  );

  /**
   * 推移グラフのシリーズデータを構築する
   * @param bloodPressureData 血圧データ
   * @returns シリーズデータ
   */
  const buildTransitionChartSeries = useCallback(
    (bloodPressureData: BloodPressure[]) => {
      return bloodPressureData.map((item) => {
        return {
          dateString: item.dateString,
          bloodPressure: [item.min, item.max],
          pulse: item.pulse,
        };
      });
    },
    []
  );

  // MARK: Event Handlers

  /**
   * 戻るボタン押下時の処理
   */
  const onClickBackButton = useCallback(() => {
    navigate(-1);
  }, []);

  /**
   * 推移グラフのデータをリロード
   */
  const onReloadTransitionData = useCallback(() => {
    initTransitionData();
  }, []);

  /**
   * 散布図データをリロード
   */
  const onReloadMeasurementData = useCallback(() => {
    initMeasurementDateData();
  }, []);

  return {
    props: {
      transitionChartData,
      scatterChartData,
      summaryData,
      japaneseFormatDateString,
    },
    handlers: {
      onClickBackButton,
      onReloadTransitionData,
      onReloadMeasurementData,
    },
  };
};
